<template>
  <leaflet-map
    ref="leafletMap"
    :markers="markers"
    :initial-zoom="initialZoom"
    :initial-bounds="mapBounds"
    :initial-centre="centre"
    :zoom-control="zoomControl"
    :scroll-wheel-zoom="scrollWheelZoom"
    :clickable="true"
    :circle="circle"
    :show-geolocation-icon="showGeolocationIcon"
    :enable-popup="enablePopup"
    :use-clustering="useClustering"
    :visible-marker="visibleMarker"
    :seen-campsites="seenCampsites"
    :fly-to="flyTo"
    :bounds-to-fit="boundsToFit"
    :map-style="userDetailsStore.mapStyleForMap"
    :show-layer-selector="showLayerSelector"
    @dragend="debouncedBoundsChanged($event)"
    @zoomend="debouncedBoundsChanged($event)"
    @marker-selected="$emit('marker-selected', $event)"
    @marker-clicked="$emit('marker-clicked', $event)"
    @map-type-changed="userDetailsStore.setMapStyle($event)"
  >
    {{ userDetailsStore.mapStyleForMap }}
    <template #popup="{ marker }">
      <slot name="popup" :marker-data="marker.data" />
    </template>
    <template #dynamicPopup>
      <slot name="dynamicPopup" />
    </template>
  </leaflet-map>
</template>

<script lang="ts">
import type * as L from 'leaflet'
import { debounce } from 'lodash-es'
import type { Pinia } from 'pinia'
import { GeoCircle } from '~/utility/geo/GeoLocation'
import { GeoBounds } from '~/utility/geo/bounds'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { logger } from '../../../../utility/logger'
import { Marker } from './types'

@Component({
  components: {
    'leaflet-map': defineAsyncComponent(() => {
      if (process.client) {
        return import('./non-autoload-components/LeafletMap.vue')
      }
    }),
  },
  emits: ['boundsChange', 'dragstart', 'marker-selected'],
  setup() {
    const userDetailsStore = useUserDataStore(usePinia() as Pinia)
    return { userDetailsStore }
  },
})
class PuMap extends Vue {
  @Prop({ required: true })
  markers: Marker[]

  @Prop({ required: false, default: 8 })
  initialZoom: number

  @Prop({ required: false })
  initialBounds?: GeoBounds

  @Prop({ required: false, default: null })
  circle: GeoCircle | null

  @Prop({ required: false, default: true })
  enablePopup: boolean

  @Prop({ required: false })
  visibleMarker: string

  @Prop({ required: false })
  centre?: string

  @Prop({ default: false })
  useClustering: boolean

  @Prop({ default: false })
  showGeolocationIcon: boolean

  @Prop({ default: false })
  zoomControl: boolean

  @Prop({ default: false })
  scrollWheelZoom: boolean

  @Prop({ default: () => [] })
  seenCampsites: string[]

  @Prop({ default: undefined })
  flyTo?: boolean

  @Prop()
  boundsToFit?: any

  @Prop({ required: false, default: false })
  showLayerSelector: boolean

  get mapBounds(): L.LatLngBoundsLiteral | null {
    logger('PuMap.mapBounds', JSON.stringify(this.initialBounds))
    return this.initialBounds
      ? [
          [this.initialBounds.northEast.lat, this.initialBounds.northEast.lng],
          [this.initialBounds.southWest.lat, this.initialBounds.southWest.lng],
        ]
      : null
  }

  boundsChanged(bounds: GeoBounds) {
    logger('PuMap.boundsChanged', JSON.stringify(bounds))
    this.$emit('boundsChange', bounds)
  }

  debouncedBoundsChanged = debounce((event) => this.boundsChanged(event), 2000)

  getBounds() {
    const leafletMap = this.$refs.leafletMap
    if (leafletMap) {
      return leafletMap.getBounds()
    }
  }
}

export default toNative(PuMap)
</script>
